import { RADIUS, SPACE, T, type Token } from '@/design/tokens';
import type { CSSProperties, ReactNode } from 'react';

export type PillSize = 'sm' | 'md';
export type PillVariant = 'tint' | 'neutral';

interface PillProps {
  children: ReactNode;
  color?: Token;
  size?: PillSize;
  variant?: PillVariant;
  style?: CSSProperties;
}

const SIZE: Record<PillSize, CSSProperties> = {
  sm: { padding: `2px ${SPACE.sm}px`, fontSize: 11 },
  md: { padding: `${SPACE.xs}px ${SPACE.sm}px`, fontSize: 12 },
};

export function Pill({
  children,
  color = 'accent',
  size = 'sm',
  variant = 'tint',
  style,
}: PillProps) {
  const sharedStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: SPACE.xs,
    borderRadius: RADIUS.pill,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    ...SIZE[size],
  };

  const variantStyle: CSSProperties =
    variant === 'neutral'
      ? {
          background: 'transparent',
          border: `1px solid ${T.border}`,
          color: T.textMuted,
        }
      : (() => {
          const c = T[color];
          return {
            background: `color-mix(in srgb, ${c} 15%, transparent)`,
            border: `1px solid color-mix(in srgb, ${c} 35%, transparent)`,
            color: c,
          };
        })();

  return <span style={{ ...sharedStyle, ...variantStyle, ...style }}>{children}</span>;
}
